import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import {
  getMyIpAssetList, getMyIpPickerList, getMyIpProjectList,
} from '$/domains/myIp/myIpService';
import { appendUniqueMyIps } from '$/hooks/myIp/useMyIpList';
import { subscribeMyIpChanged } from '$/domains/myIp/changeEvents';
import type { MyIpChangeReason } from '$/domains/myIp/changeEvents';
import type { MyIpItem } from '$/domains/myIp/types';

const PROJECT_REFRESH_REASONS: readonly MyIpChangeReason[] = ['binding'];

interface State {
  key: string;
  assetType?: 'personal' | 'official' | 'all';
  list: MyIpItem[];
  status: 'idle' | 'loading' | 'success' | 'error';
  total: number;
  hasMore: boolean;
  loadingMore: boolean;
  error?: unknown;
}

/** 缓存只存在于当前挂载实例及同一账号/分类/检索词内，绝不跨身份复用。 */
function empty(key: string): State {
  return {
    key, list: [], status: 'idle', total: 0, hasMore: false, loadingMore: false,
  };
}

/** 业务拒绝不是网络失败，不能将 HTTP 400 中尚未统一的权限码误当作可保留缓存的断网。 */
function mustClearCache(error: unknown) {
  const value = error as { status?: number; code?: number; response?: { status?: number; }; } | null;
  const status = value?.status ?? value?.response?.status ?? 0;
  return (status >= 400 && status < 500 && status !== 408 && status !== 429)
    || (status === 200 && typeof value?.code === 'number' && value.code !== 0);
}

/** 只将传输失败、超时及服务不可用归为网络异常；业务拒绝、取消和解析异常不误报。 */
function isNetworkFailure(error: unknown): boolean {
  const value = error as {
    name?: string; code?: string | number; status?: number; isAxiosError?: boolean;
    request?: unknown; response?: { status?: number; };
  } | null;
  if (!value || value.name === 'AbortError' || value.code === 'ERR_CANCELED') return false;
  const status = value.status ?? value.response?.status;
  if (status) return status === 408 || (status >= 500 && status < 600);
  return ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(String(value.code))
    || (value.isAxiosError === true && Boolean(value.request) && !value.response);
}

interface MyIpPageSource {
  scene: 'asset_lib' | 'picker' | 'project';
  keyword?: string;
  projectId?: string;
  roomId?: string;
  officialFallback?: boolean;
}

/** 根据入口选择列表；只有已确认无个人 IP 的资产空态才读取官方候选。 */
async function requestMyIpPage(
  source: MyIpPageSource,
  assetType: 'personal' | 'official' | 'all',
  page?: number,
  signal?: AbortSignal,
) {
  if (source.scene === 'project') {
    const result = await getMyIpProjectList({
      projectId: source.projectId || '', roomId: source.roomId || '', signal,
    });
    // 头像栏展示全部关联 IP；角色选择器仍可在本地按个人/官方分组。
    const list = assetType === 'all' ? result.list : result.list.filter((item) => item.assetType === assetType);
    return { list, total: list.length, assetType };
  }
  if (assetType === 'all') throw new Error('All IPs require a project context');
  if (source.scene === 'picker') return { ...await getMyIpPickerList({ assetType, page, signal }), assetType };
  const result = await getMyIpAssetList({
    assetType, keyword: source.keyword, page, signal,
  });
  if (source.officialFallback && assetType === 'personal' && !source.keyword && !page
    && result.total === 0 && result.list.length === 0 && !signal?.aborted) {
    return { ...await getMyIpAssetList({ assetType: 'official', signal }), assetType: 'official' as const };
  }
  return { ...result, assetType };
}

/** IP 候选列表共享分页生命周期，并按账号、数据源、分类及项目隔离状态。 */
function useMyIpPageList(
  scopeKey: string,
  enabled: boolean,
  assetType: 'personal' | 'official' | 'all',
  source: MyIpPageSource,
  onNetworkError?: () => void,
  refreshReasons?: readonly MyIpChangeReason[],
) {
  const keyword = source.keyword?.trim() || '';
  const {
    scene, projectId, roomId, officialFallback,
  } = source;
  const key = JSON.stringify([scopeKey, enabled, scene, assetType, keyword, projectId, roomId, officialFallback]);
  const [state, setState] = useState<State>(() => empty(key));
  const [revision, setRevision] = useState(0);
  const currentKey = useRef(key);
  currentKey.current = key;
  const controller = useRef<AbortController>();
  const epoch = useRef(0);
  const page = useRef(1);
  const received = useRef(0);
  const pending = useRef(false);
  const networkErrorHandler = useRef(onNetworkError);
  networkErrorHandler.current = onNetworkError;

  /** 明确失效正在进行的分页，订阅方只会重载自身当前身份的数据。 */
  const refresh = useCallback(() => {
    epoch.current += 1;
    controller.current?.abort();
    setRevision((value) => value + 1);
  }, []);
  /** 项目绑定列表只在关系变更后刷新，角色内容批量保存无需重新拉取关联关系。 */
  useEffect(() => subscribeMyIpChanged((reason) => {
    if (!refreshReasons || refreshReasons.includes(reason)) refresh();
  }), [refresh, refreshReasons]);

  useEffect(() => {
    const abort = new AbortController();
    controller.current = abort;
    epoch.current += 1;
    const requestEpoch = epoch.current;
    page.current = 1;
    received.current = 0;
    pending.current = false;
    setState((previous) => ({
      // 同一上下文刷新期间保留已展示列表，项目 IP 栏不会因请求中的空数组闪烁。
      ...(enabled && previous.key === key ? previous : empty(key)),
      status: enabled ? 'loading' : 'idle',
      loadingMore: false,
      error: undefined,
    }));
    /** 即使网络库忽略 abort，也禁止旧空间或旧关键词请求提交。 */
    const isCurrent = () => !abort.signal.aborted && epoch.current === requestEpoch && currentKey.current === key;
    if (enabled) {
      const request = requestMyIpPage({
        scene, keyword, projectId, roomId, officialFallback,
      }, assetType, undefined, abort.signal);
      request.then((result) => {
        if (!isCurrent()) return;
        received.current = result.list.length;
        setState({
          key,
          assetType: result.assetType,
          list: appendUniqueMyIps([], result.list),
          total: result.total,
          status: 'success',
          hasMore: result.list.length > 0 && received.current < result.total,
          loadingMore: false,
        });
      }).catch((error) => {
        if (!isCurrent()) return;
        setState((previous) => ({ ...(mustClearCache(error) ? empty(key) : previous), status: 'error', error }));
        if (assetType === 'personal' && isNetworkFailure(error)) networkErrorHandler.current?.();
      });
    }
    return () => abort.abort();
  }, [key, enabled, scene, assetType, keyword, projectId, roomId, officialFallback, revision]);

  /** 失败保留当前页码和已加载卡片，重试同一页，空页终止分页。 */
  const loadMore = async () => {
    const abort = controller.current;
    if (!enabled || currentKey.current !== key || state.key !== key || state.status !== 'success'
      || !state.hasMore || pending.current || !abort || abort.signal.aborted) return;
    const requestEpoch = epoch.current;
    const nextPage = page.current + 1;
    pending.current = true;
    setState((previous) => ({ ...previous, loadingMore: true, error: undefined }));
    /** 查询或刷新变化后的分页成功和失败均丢弃。 */
    const isCurrent = () => !abort.signal.aborted && epoch.current === requestEpoch && currentKey.current === key;
    try {
      const result = await requestMyIpPage({
        scene, keyword, projectId, roomId,
      }, state.assetType ?? assetType, nextPage, abort.signal);
      if (!isCurrent()) return;
      page.current = nextPage;
      received.current += result.list.length;
      setState((previous) => ({
        ...previous,
        list: appendUniqueMyIps(previous.list, result.list),
        total: result.total,
        hasMore: result.list.length > 0 && received.current < result.total,
        loadingMore: false,
      }));
    } catch (error) {
      if (!isCurrent()) return;
      setState((previous) => ({ ...(mustClearCache(error) ? { ...empty(key), status: 'error' as const } : previous), loadingMore: false, error }));
      if (assetType === 'personal' && isNetworkFailure(error)) networkErrorHandler.current?.();
    } finally {
      if (isCurrent()) pending.current = false;
    }
  };
  const visible = state.key === key && enabled ? state : empty(key);
  return {
    ...visible, loadedPages: visible.status === 'success' ? page.current : 0, refresh, loadMore,
  };
}

/** 资产库独立分页；空态兜底显式启用，官方来源刷新不保留可能已下架的卡片。 */
export function useMyIpAssetList(
  scopeKey: string,
  enabled: boolean,
  assetType: 'personal' | 'official',
  keyword: string,
  onNetworkError?: () => void,
  options: { officialFallback?: boolean; } = {},
) {
  return useMyIpPageList(scopeKey, enabled, assetType, {
    scene: 'asset_lib', keyword, officialFallback: options.officialFallback,
  }, onNetworkError);
}

/** Home 使用 picker；房间可展示全部关联 IP，或按个人/官方分类。 */
export function useMyIpPickerList(
  scopeKey: string,
  enabled: boolean,
  assetType: 'personal' | 'official' | 'all',
  context: { projectId?: string; roomId?: string; } = {},
) {
  const isProjectSource = Boolean(context.projectId);
  return useMyIpPageList(scopeKey, enabled, assetType, {
    scene: isProjectSource ? 'project' : 'picker',
    projectId: context.projectId,
    roomId: context.roomId,
  }, undefined, isProjectSource ? PROJECT_REFRESH_REASONS : undefined);
}
